'use strict';

/**
 * NACRE Wave 6.3a — pin-ui-client (pure state machine)
 *
 * The brain of the PIN protection UX. Pure state machine — no DOM, no
 * direct HTTP calls. All side effects go through INJECTED dependencies
 * so this module is fully testable in Node and reusable in the browser.
 *
 * The DOM glue (Wave 6.3b) wires this state machine to the actual modal
 * overlay, the host drawer header lock button, and the auto-lock timer
 * tick from setInterval.
 *
 * STATES:
 *   NO_PIN        — module has no PIN protection set up. Master is
 *                   accessible without ceremony (assumed already in session).
 *   PIN_LOCKED    — module has a PIN. Master is NOT in session memory.
 *                   Cannot access until unlockPin() succeeds.
 *   PIN_UNLOCKED  — module has a PIN AND was just unlocked. Master is in
 *                   session memory. Auto-lock will fire after autoLockMs
 *                   of inactivity.
 *
 * TRANSITIONS:
 *   NO_PIN ──setupPin(pin)──▶ PIN_UNLOCKED
 *   PIN_LOCKED ──unlockPin(pin)──▶ PIN_UNLOCKED
 *   PIN_UNLOCKED ──lock() / autoLock──▶ PIN_LOCKED
 *   PIN_UNLOCKED ──removePin()──▶ NO_PIN
 *
 * USAGE FROM THE BROWSER:
 *
 *   const session = createPinUiSession({
 *     moduleId: 'notes',
 *     pinClient: window.OysterVault,
 *     kdfProfile: window.OysterVault.KDF_PROFILES.DESKTOP,
 *     autoLockMs: 5 * 60 * 1000,
 *     getStoredEnvelope: async () => {
 *       const res = await fetch('/auth/vault/me/pin/notes', { credentials: 'same-origin' });
 *       if (res.status === 404) return null;
 *       const data = await res.json();
 *       return data.pinWrap;
 *     },
 *     putStoredEnvelope: async (env) => {
 *       await fetch('/auth/vault/me/pin/notes', {
 *         method: 'PUT',
 *         credentials: 'same-origin',
 *         headers: { 'content-type': 'application/json' },
 *         body: JSON.stringify({ pinWrap: env }),
 *       });
 *     },
 *     removeStoredEnvelope: async () => {
 *       await fetch('/auth/vault/me/pin/notes', { method: 'DELETE', credentials: 'same-origin' });
 *     },
 *     getModuleMasterFromSession: () => window.OysterVault.session.moduleMasters.notes,
 *     setModuleMasterInSession: (m) => { window.OysterVault.session.moduleMasters.notes = m; },
 *     clearModuleMasterFromSession: () => { delete window.OysterVault.session.moduleMasters.notes; },
 *   });
 *
 *   await session.refresh();  // load current state from server
 *
 *   if (session.state === 'PIN_LOCKED') {
 *     // Show PIN entry modal — when user enters PIN, call session.unlockPin(pin)
 *   }
 */

const STATES = Object.freeze({
  NO_PIN: 'NO_PIN',
  PIN_LOCKED: 'PIN_LOCKED',
  PIN_UNLOCKED: 'PIN_UNLOCKED',
});

const DEFAULT_AUTO_LOCK_MS = 5 * 60 * 1000;  // 5 minutes

// T3228A (S315 §10.2 rule 2) — the lock state could not be read. Never a state of
// its own here: refresh() REJECTS with this code and leaves the state where it was,
// because "could not be read" read as NO_PIN invites creating a lock (S407's hazard).
const ERR_MODULE_LOCK_STATE_UNKNOWN = 'ERR_MODULE_LOCK_STATE_UNKNOWN';

function lockStateUnknown(cause, answer) {
  if (cause && cause.code === ERR_MODULE_LOCK_STATE_UNKNOWN) return cause;
  const err = new Error('Module Lock state unknown: '
    + (cause ? (cause.message || String(cause)) : 'presence answered ' + String(answer)));
  err.code = ERR_MODULE_LOCK_STATE_UNKNOWN;
  if (cause) err.cause = cause;
  return err;
}

function createPinUiSession({
  moduleId,
  pinClient,
  kdfProfile,
  autoLockMs,
  getStoredEnvelope,
  getStoredPresence,
  putStoredEnvelope,
  removeStoredEnvelope,
  getModuleMasterFromSession,
  setModuleMasterInSession,
  clearModuleMasterFromSession,
  now,
  isSaveInFlight,
}) {
  if (!moduleId) throw new TypeError('moduleId required');
  if (!pinClient) throw new TypeError('pinClient required');
  if (typeof getStoredEnvelope !== 'function') throw new TypeError('getStoredEnvelope required');
  if (typeof putStoredEnvelope !== 'function') throw new TypeError('putStoredEnvelope required');
  if (typeof removeStoredEnvelope !== 'function') throw new TypeError('removeStoredEnvelope required');
  if (typeof getModuleMasterFromSession !== 'function') throw new TypeError('getModuleMasterFromSession required');
  if (typeof setModuleMasterInSession !== 'function') throw new TypeError('setModuleMasterInSession required');
  if (typeof clearModuleMasterFromSession !== 'function') throw new TypeError('clearModuleMasterFromSession required');
  // T3228A (S315 §10.2 rule 1) — OPTIONAL presence reader. When supplied, refresh()
  // asks it and NEVER getStoredEnvelope(): refresh() only needs "is there a Key",
  // and the envelope read is the full S315 extraction (a minted grant, a spent
  // unlock-throttle unit, the grindable material served, an `unlock` audit row).
  // Every drawer open refreshes, so the extraction had become ambient again — the
  // 11th open in 15 minutes was refused 429 and logged as a failed unlock, and a
  // keyed module's owner could not unlock it. unlockPin() keeps getStoredEnvelope():
  // the material is fetched exactly when it is used. Absent ⇒ the legacy refresh,
  // for injectors that ship separately from this bundle.
  if (getStoredPresence !== undefined && typeof getStoredPresence !== 'function') {
    throw new TypeError('getStoredPresence must be a function when supplied');
  }

  const _now = typeof now === 'function' ? now : () => Date.now();
  const _autoLockMs = autoLockMs || DEFAULT_AUTO_LOCK_MS;
  // T315 P5 (LD-11) — optional save-in-flight predicate. When it reports true,
  // relock DEFERS (relockPending) instead of clearing the master mid-encrypt
  // (which would silently corrupt a save). drainRelock() fires the deferred
  // lock once the save chain has drained. Absent ⇒ proven path unchanged.
  const _isSaveInFlight = typeof isSaveInFlight === 'function' ? isSaveInFlight : null;
  let _relockPending = false;

  let state = STATES.NO_PIN;
  let lastActivityAt = _now();
  const subscribers = new Set();

  function setState(next) {
    if (state === next) return;
    state = next;
    for (const cb of subscribers) {
      try { cb(state); } catch (_e) {}
    }
  }

  /**
   * Recompute state from the current backend snapshot. Call after any
   * external change (initial load, server update from another tab, etc.).
   *
   * Always fires the state event after refresh, even if state is unchanged,
   * so that subscribers wired before the first refresh receive a "ready"
   * signal with the current state.
   */
  /**
   * Is a Key stored for this module? With a presence reader: exactly `true` or
   * `false` is an answer; a throw or any other value is UNKNOWN and rejects (the
   * caller's state does not move). Without one: the legacy envelope truthiness.
   */
  async function readPresence() {
    if (!getStoredPresence) return !!(await getStoredEnvelope());
    let present;
    try {
      present = await getStoredPresence();
    } catch (e) {
      throw lockStateUnknown(e);
    }
    if (present !== true && present !== false) throw lockStateUnknown(null, present);
    return present;
  }

  async function refresh() {
    const present = await readPresence();
    const masterInSession = getModuleMasterFromSession();
    let newState;
    if (!present) {
      newState = STATES.NO_PIN;
    } else if (masterInSession) {
      newState = STATES.PIN_UNLOCKED;
      lastActivityAt = _now();
    } else {
      newState = STATES.PIN_LOCKED;
    }
    // Force-fire even if unchanged so first-refresh sends a "ready" event
    if (state === newState) {
      // Fire manually since setState short-circuits on no change
      for (const cb of subscribers) {
        try { cb(state); } catch (_e) {}
      }
    } else {
      setState(newState);
    }
  }

  /**
   * Set up PIN protection for the first time. The module master must
   * already be in session memory (you can't put a PIN on a module you
   * don't have access to).
   */
  async function setupPin(pin) {
    if (state !== STATES.NO_PIN) {
      throw new Error('setupPin only valid in NO_PIN state (current: ' + state + ')');
    }
    const validation = pinClient.validatePinFormat(pin);
    if (!validation.ok) {
      throw new Error('Invalid PIN: ' + validation.error);
    }
    const moduleMaster = getModuleMasterFromSession();
    if (!moduleMaster) {
      // LD-4 (T219): The real precondition is the ACCOUNT master key being unlocked
      // (happens at login). There is no user-facing "unlock the module" step.
      throw new Error('Cannot setup PIN: account master key not unlocked. Please log in again to restore your session.');
    }

    // GOTCHA-575 defect (c) — CLOSE THE GATE BEFORE THE EXPENSIVE WORK.
    // The `state !== NO_PIN` check above IS the dedupe gate, but the state used
    // to transition only AFTER `await putStoredEnvelope(...)`. While the first
    // caller was awaiting the network, state was still NO_PIN, so every
    // concurrent caller sailed straight through the gate. Observed live
    // (account 001, 2026-07-21): 4 duplicate PUTs in 12s ⇒ 4x Argon2id ⇒ ~16s of
    // frozen UI ⇒ 4 duplicate lock-audit entries. The transition therefore has
    // to happen BEFORE the KDF and the write — both are the expensive part.
    //
    // Rolled back on failure so a failed save never wedges the module into a
    // false "locked" state: the user must always be able to retry.
    setState(STATES.PIN_UNLOCKED);
    lastActivityAt = _now();
    try {
      const envelope = pinClient.buildPinWrapEnvelope({
        pin,
        moduleMaster,
        kdfProfile,
      });
      await putStoredEnvelope(envelope);
      // Master stays in session — user is "logged in" to the module right now
    } catch (e) {
      setState(STATES.NO_PIN);
      throw e;
    }
  }

  /**
   * Unlock a PIN-protected module. Fetches the envelope from the server
   * and unwraps it client-side. The module master is loaded into session
   * memory on success.
   */
  async function unlockPin(pin) {
    if (state !== STATES.PIN_LOCKED) {
      throw new Error('unlockPin only valid in PIN_LOCKED state (current: ' + state + ')');
    }
    if (typeof pin !== 'string' || pin.length === 0) {
      throw new Error('Invalid PIN: must be a non-empty string');
    }
    const envelope = await getStoredEnvelope();
    if (!envelope) {
      throw new Error('No PIN envelope on server — was the PIN removed?');
    }
    let master;
    try {
      master = pinClient.unwrapPinEnvelope({ pin, pinWrap: envelope, kdfProfile });
    } catch (e) {
      // Wrong PIN or corrupt envelope — stay in PIN_LOCKED
      throw new Error('Wrong PIN');
    }
    setModuleMasterInSession(master);
    setState(STATES.PIN_UNLOCKED);
    lastActivityAt = _now();
  }

  /**
   * Manually lock the module. Wipes the master from session memory.
   * The envelope on the server is preserved — user can unlock again with
   * the same PIN.
   */
  function lock() {
    if (state !== STATES.PIN_UNLOCKED) {
      // Already locked / no PIN — nothing to do
      return;
    }
    // T315 P5 (LD-11): never clear the master while a save is mid-encrypt.
    // Defer; drainRelock() completes the lock once the save chain drains.
    if (_isSaveInFlight && _isSaveInFlight()) {
      _relockPending = true;
      return;
    }
    _relockPending = false;
    clearModuleMasterFromSession();
    setState(STATES.PIN_LOCKED);
  }

  /**
   * T315 P5 (LD-11): re-attempt a deferred relock once the in-flight save has
   * drained. The core-client save chain calls this from its `finally`. No-op
   * when nothing is pending or a save is still in flight.
   */
  function drainRelock() {
    if (!_relockPending) return;
    if (_isSaveInFlight && _isSaveInFlight()) return; // still saving — keep pending
    lock();
  }

  function isRelockPending() {
    return _relockPending === true;
  }

  /**
   * Remove PIN protection entirely. Master remains in session memory
   * (the user just opted out of PIN — they don't lose access).
   */
  async function removePin() {
    if (state !== STATES.PIN_UNLOCKED) {
      throw new Error('removePin only valid in PIN_UNLOCKED state (current: ' + state + ')');
    }
    await removeStoredEnvelope();
    setState(STATES.NO_PIN);
  }

  /**
   * Returns the unwrapped module master if available. Throws if locked.
   * Resets the auto-lock idle timer on success.
   */
  function getModuleMaster() {
    if (state === STATES.PIN_LOCKED) {
      throw new Error('Module is locked. Unlock with PIN first.');
    }
    const master = getModuleMasterFromSession();
    if (!master) {
      throw new Error('Module master not in session despite state=' + state);
    }
    lastActivityAt = _now();
    return master;
  }

  /**
   * Tick from a setInterval (or any external clock). Checks if the
   * auto-lock window has elapsed and triggers lock() if so.
   *
   * The DOM glue calls this every 30 seconds or so. It's safe to call
   * arbitrarily often — it's a no-op if state is not PIN_UNLOCKED.
   */
  function tick() {
    if (state !== STATES.PIN_UNLOCKED) return;
    const elapsed = _now() - lastActivityAt;
    if (elapsed >= _autoLockMs) {
      lock();
    }
  }

  /**
   * Subscribe to state changes. Returns an unsubscribe function.
   *
   * Convention (matches React/RxJS): the callback fires IMMEDIATELY on
   * subscription with the current state, AND on every subsequent
   * transition. This lets UI code wire up once and render the right
   * thing whether state has been initialized yet or not.
   *
   * However, the immediate fire on subscribe happens lazily — only AFTER
   * the next refresh() call, NOT during subscribe(). This avoids firing
   * before the caller is ready and matches the test expectation that
   * the first event reflects "the state after the first refresh."
   */
  function onStateChange(callback) {
    subscribers.add(callback);
    return () => subscribers.delete(callback);
  }

  // Reactive state object so callers can do `session.state` directly
  return {
    moduleId,
    get state() { return state; },
    refresh,
    setupPin,
    unlockPin,
    lock,
    removePin,
    getModuleMaster,
    tick,
    drainRelock,
    isRelockPending,
    onStateChange,
  };
}

module.exports = {
  // T643 (LD-2) — the vault-lock names are canonical. `createPinUiSession` and
  // `PIN_STATES` stay exported because public/auth/vault-bundle.js is a
  // COMMITTED artifact rebuilt by the integrator, so a page can be served
  // against a bundle that predates this rename; key-overlay.js prefers the new
  // name and falls back. The STATE VALUES ('NO_PIN' etc.) are deliberately NOT
  // renamed — they are compared as literals by pages that ship separately from
  // the bundle, which makes them an enum wire format, not UX copy.
  createVaultLockSession: createPinUiSession,
  VAULT_LOCK_STATES: STATES,
  createPinUiSession,
  STATES,
  DEFAULT_AUTO_LOCK_MS,
  ERR_MODULE_LOCK_STATE_UNKNOWN,
};
